import type { GameState, Move } from "./logic"

import { E2E_SORTABALLS } from "../config/constants"
import e2ePuzzlesData from "../../e2e-puzzles/sortaballs-8-colors.json"
import puzzlesData from "../../sortaballs-8-colors-puzzles.json"

export interface Puzzle {
  // Content address of the initial board (`game.state`), stable across files
  // and generator runs. Distinct from `id` (the numeric navigation index).
  boardId: string
  // First few moves of the solver's found solution (0-based plate indices),
  // used by the in-game help feature. Empty for puzzles generated before this
  // property existed.
  correctMoves: Move[]
  difficulty: "easy" | "hard" | "medium"
  id: number
  solutionLength: number
  state: GameState
  statesExplored: number
  timeElapsed: number
  timestamp: string
}

interface PuzzleJsonData {
  game: {
    correctMoves?: { from: number; to: number }[]
    solutionLength: number
    state: string[][]
    statesExplored: number
    timeElapsed: number
    timestamp: string
  }
  id?: string
  numberOfMoves: number
}

export function classifyDifficulty(moves: number): "easy" | "hard" | "medium" {
  if (moves <= 38) return "easy"
  if (moves <= 41) return "medium"
  return "hard"
}

const source = E2E_SORTABALLS ? e2ePuzzlesData : puzzlesData

// Parse the puzzle JSON once at module load so every caller shares the same
// object references (getPuzzleById must return a stable reference per id —
// components rely on reference equality to detect puzzle changes).
// SAFETY: puzzlesData is a JSON array of PuzzleJsonData
const PUZZLES: Puzzle[] = (source as PuzzleJsonData[]).map((p, i)=> ({
  boardId: p.id ?? "",
  correctMoves: p.game.correctMoves ?? [],
  difficulty: classifyDifficulty(p.numberOfMoves),
  id: i + 1,
  solutionLength: p.numberOfMoves,
  // SAFETY: puzzle JSON state conforms to GameState
  state: p.game.state as GameState,
  statesExplored: p.game.statesExplored,
  timeElapsed: p.game.timeElapsed,
  timestamp: p.game.timestamp,
}))

export function getPuzzleById(id: number): Puzzle | undefined {
  // ids are dense 1..N (assigned as index + 1 above), so direct index read.
  return PUZZLES[id - 1]
}

export function getTotalPuzzleCount(): number {
  return PUZZLES.length
}
